import { atom, injectStore, api, injectAtomValue, injectEffect, injectAtomInstance } from '@zedux/react';
import { IUserBasic } from '../types';
import { contactsListAtom, socketAtom } from './socket';

const supportUserInfo = {
    id: 0,
    username: 'support',
    fullname: 'پشتیبانی',
    avatar_url: '',
    last_seen: false
} as IUserBasic

// The main store that holds all known users by ID
export const usersMapAtom = atom('usersMap', () => {
    const store = injectStore<Record<number, IUserBasic>>({});
    const socket = injectAtomValue(socketAtom);
    const socketInstance = injectAtomInstance(socketAtom);

    const addUsers = (users: IUserBasic | IUserBasic[]) => {
        const userList = Array.isArray(users) ? users : [users];

        store.setState(state => {
            const newState = { ...state };
            userList.forEach(user => {
                if (newState[user.id]) {
                    newState[user.id] = { ...newState[user.id], ...user };
                } else {
                    newState[user.id] = user;
                }
            });
            return newState;
        });
    };

    injectEffect(() => {
        if (!socket) return;

        const handleUserList = (users: IUserBasic[]) => {
            console.log('socket: user_list received ', users);
            if (Array.isArray(users)) {
                addUsers(users);
            }
        };

        socket.on('user_list', handleUserList);
        return () => {
            socket.off('user_list', handleUserList);
        };
    }, [socket]);

    injectEffect(() => {
        // Add support user info on init
        addUsers(supportUserInfo)
    }, [])

    const updateLastSeen = (userId: number) => {
        store.setState(state => {
            const user = state[userId];
            if (!user) return state;

            return {
                ...state,
                [userId]: {
                    ...user,
                    last_seen: Date.now()
                }
            };
        });
    };

    const sendRequestUserInfo = async (userId: number) => {
        if (store.getState()[userId]) {
            return; // We already have this user's info
        }

        const socket = socketInstance.getState();
        if (socket) {
            try {
                const { userInfo } = await socket.emitWithAck('request_user_info', userId);
                if (userInfo) {
                    addUsers(userInfo as IUserBasic);
                }

            } catch (err) {
                console.error("Error requesting user info:", err);
            }

        }
    };

    return api(store).setExports({
        addUsers,
        updateLastSeen,
        sendRequestUserInfo
    });
});

// The atom that holds lists of IDs for each category
// Parameter can be 'staffs', 'guests', etc.
// export const userCategoryAtom = atom('userCategory', (category: string) => {
//     const store = injectStore<number[]>([]);

//     const setIds = (ids: number[]) => {
//         store.setState(ids)
//     }

//     return api(store).setExports({
//         setIds
//     })
// });

// Selector that returns a single user by id, or null if not found
export const userByIdSelector = ({ get, getInstance }: any, userId: number | null): IUserBasic | null => {
    const usersMap = get(usersMapAtom);
    if (userId === null) return null

    if (!usersMap[userId]) {
        const { sendRequestUserInfo } = getInstance(usersMapAtom).exports
        sendRequestUserInfo(userId)
    }

    return usersMap[userId] ?? null
};

// The selector that combines them
// It takes the same parameter (category) and returns the list of user objects
export const usersByCategorySelector = ({ get }: any, category: string) => {
    const groups = get(contactsListAtom)
    const usersMap = get(usersMapAtom)
    const ids = groups[category] ?? []

    return ids.map((id: number) => usersMap[id]).filter((u: IUserBasic | undefined) => u !== undefined) as IUserBasic[]
}
